import { Link } from '../../router/Link';
import { Button, Eyebrow, Notice, Panel, Spinner, Toggle } from '../../ui';
import { useLogin } from './useLogin';
import type { LoginView, PermissionLine, SessionReadout } from './model';
import layout from '../../ui/layout.module.css';
import { useCopy } from '../../strings/useCopy';
import styles from './login.module.css';

/**
 * §8's sign-in page — the `/authorize_token` grant 0g proved, told as a page a reader can
 * consent on. Renders props (R13): every decision is in `model.ts`, every effect in `useLogin.ts`, and
 * the session state itself comes from core.
 */
export function LoginView({ next }: { readonly next: string | null }) {
  const { view, busy, handlers } = useLogin(next);
  const copy = useCopy().text.login;
  const words = copy.page;

  return (
    <div className={layout.page}>
      <header className={styles.head}>
        <h1 className={styles.title}>{view.headline}</h1>
        <p className={styles.lead}>{view.explanation}</p>
      </header>

      {view.notice === null ? null : (
        <Notice tone={view.notice.tone} live={view.notice.live}>
          {view.notice.text}
        </Notice>
      )}

      {view.signedIn && view.session !== null ? (
        <SessionPanel
          session={view.session}
          view={view}
          busy={busy}
          onSignOut={handlers.signOut}
          onReauthorize={handlers.signIn}
          onRecheck={handlers.recheck}
          onAutoRenew={handlers.setAutoRenew}
        />
      ) : (
        <GrantPanel
          view={view}
          busy={busy}
          onSignIn={handlers.signIn}
          onAutoRenew={handlers.setAutoRenew}
        />
      )}

      <PermissionList permissions={view.permissions} showGrants={view.signedIn} />

      <p className={styles.footnote}>
        {/* Not a disclaimer. Both halves are properties of the design a reader can check: the grant is
            the instance's own page, and the token is scoped and expiring rather than a password. */}
        {words.footnoteBefore} {view.instanceHost ?? copy.explanation.theInstance}{' '}
        {words.footnoteAfter}
      </p>
    </div>
  );
}

function GrantPanel({
  view,
  busy,
  onSignIn,
  onAutoRenew,
}: {
  readonly view: LoginView;
  readonly busy: boolean;
  readonly onSignIn: () => void;
  readonly onAutoRenew: (on: boolean) => void;
}) {
  const words = useCopy().text.login.page;

  return (
    <Panel as="section" pad="lg" className={styles.panel} ariaLabel={words.grantPanelLabel}>
      <RenewalOption view={view} busy={busy} onAutoRenew={onAutoRenew} />
      <div className={styles.actions}>
        <Button variant="primary" onClick={onSignIn} disabled={busy || !view.ready}>
          {view.signInLabel}
        </Button>
        {busy ? <Spinner /> : null}
      </div>
      {view.returnHref === null ? null : (
        <p className={styles.note}>
          {words.returnAfter(view.returnLabel ?? words.whereYouWere)}
        </p>
      )}
      {view.ready ? null : <p className={styles.note}>{words.checkingSession}</p>}
    </Panel>
  );
}

/**
 * The renewal option (2026-08-02). Above the sign-in actions in both panels, because it changes
 * what the button *asks for*: on, the grant carries one more scope and the session replaces itself
 * before its 30 days run out; off, the shell asks the reader to sign in again instead. The
 * permission list below grows its sixth row the moment this is on — the ask is never wider than
 * what that list shows.
 */
function RenewalOption({
  view,
  busy,
  onAutoRenew,
}: {
  readonly view: LoginView;
  readonly busy: boolean;
  readonly onAutoRenew: (on: boolean) => void;
}) {
  const words = useCopy().text.login.page;

  return (
    <Toggle
      id="login-auto-renew"
      label={words.renewLabel}
      detail={words.renewDetail}
      checked={view.autoRenew}
      onChange={onAutoRenew}
      disabled={busy}
    />
  );
}

function SessionPanel({
  session,
  view,
  busy,
  onSignOut,
  onReauthorize,
  onRecheck,
  onAutoRenew,
}: {
  readonly session: SessionReadout;
  readonly view: LoginView;
  readonly busy: boolean;
  readonly onSignOut: () => void;
  readonly onReauthorize: () => void;
  readonly onRecheck: () => void;
  readonly onAutoRenew: (on: boolean) => void;
}) {
  const words = useCopy().text.login.page;

  return (
    <Panel as="section" pad="lg" className={styles.panel} ariaLabel={words.sessionPanelLabel}>
      <Eyebrow as="h2">{words.sessionHeading}</Eyebrow>

      {/* A label/value list rather than sentences, and that is deliberate: an English verb in front of
          a locale-formatted relative date is 4e's known defect, and a `<dl>` never builds that phrase. */}
      <dl className={styles.facts}>
        {session.username === null ? null : (
          <>
            <dt className={styles.factKey}>{words.factAccount}</dt>
            <dd className={styles.factValue}>{session.username}</dd>
          </>
        )}
        <dt className={styles.factKey}>{words.factInstance}</dt>
        <dd className={styles.factValue}>{view.instanceHost ?? words.factMissing}</dd>
        {session.expiry === null ? null : (
          <>
            <dt className={styles.factKey}>{words.factExpires}</dt>
            <dd className={styles.factValue} title={session.expiryTitle ?? undefined}>
              {session.expiry}
            </dd>
          </>
        )}
        {session.granted === null ? null : (
          <>
            <dt className={styles.factKey}>{words.factGranted}</dt>
            <dd className={styles.factValue} title={session.grantedTitle ?? undefined}>
              {session.granted}
            </dd>
          </>
        )}
      </dl>

      <VerifyLine view={view} />

      {/* Here too: flipping it and pressing "Sign in again" is how an existing session changes its
          mind about renewal, in either direction. */}
      <RenewalOption view={view} busy={busy} onAutoRenew={onAutoRenew} />

      {/*
       * Nothing here is `primary`, deliberately. On a page you have already signed in to, the loudest
       * control would be the one that undoes it — and re-authorizing a working session is not an action
       * to encourage either. The primary button belongs to the *grant* panel, where there is one thing
       * to do.
       */}
      <div className={styles.actions}>
        <Button onClick={onSignOut} disabled={busy}>
          {words.signOut}
        </Button>
        <Button variant="subtle" onClick={onReauthorize} disabled={busy}>
          {view.signInLabel}
        </Button>
        <Button variant="ghost" onClick={onRecheck} disabled={busy}>
          {words.checkAgain}
        </Button>
      </div>

      <p className={styles.note}>{words.signOutNote}</p>

      {view.returnHref === null ? null : (
        <Link className={styles.continue} href={view.returnHref}>
          {words.continueTo(view.returnLabel ?? words.whereYouWere)}
        </Link>
      )}
    </Panel>
  );
}

/** What the one authenticated call found. The page's answer to "yes, but does it actually work?". */
function VerifyLine({ view }: { readonly view: LoginView }) {
  const words = useCopy().text.login.page;

  if (view.verify.kind === 'checking') {
    return (
      // The spinner is a sibling of the sentence rather than inside it: `Spinner` is a `<div>`, and a
      // block inside a `<p>` is invalid HTML that React reports as a hydration error. Same shape as
      // the resolver's waiting state.
      <div className={styles.verify} aria-busy>
        <Spinner />
        <p role="status">{words.verifyChecking}</p>
      </div>
    );
  }
  if (view.verify.kind === 'ok') {
    return (
      <p className={styles.verifyOk} role="status">
        {view.verify.subscriptions === 0
          ? words.verifyOkEmpty
          : words.verifyOk(view.verify.subscriptions)}
      </p>
    );
  }
  if (view.verify.kind === 'failed') {
    return (
      <p className={styles.verifyBad} role="status">
        {view.verify.signedOut ? words.verifyRefusedSignedOut : words.verifyRefused}
      </p>
    );
  }
  return null;
}

/**
 * R9's scope list, in words and with the raw grant beside each row.
 *
 * `showGrants` is what makes the `under-scoped` state visible per line rather than as a count: the
 * reader can see *which* permission is missing, which is the difference between a warning and an
 * instruction.
 */
function PermissionList({
  permissions,
  showGrants,
}: {
  readonly permissions: readonly PermissionLine[];
  readonly showGrants: boolean;
}) {
  const words = useCopy().text.login.page;

  return (
    <Panel as="section" pad="lg" className={styles.panel} ariaLabel={words.permissionsPanelLabel}>
      <Eyebrow as="h2">
        {showGrants ? words.permissionsHeadingGranted : words.permissionsHeadingWanted}
      </Eyebrow>
      <ul className={styles.permissions}>
        {permissions.map((line) => (
          <li className={styles.permission} key={line.id}>
            <p className={styles.permissionTitle}>
              {line.title}
              {showGrants ? (
                <span className={line.granted ? styles.grantOk : styles.grantMissing}>
                  {line.granted ? words.granted : words.notGranted}
                </span>
              ) : null}
            </p>
            <p className={styles.permissionDetail}>{line.detail}</p>
            <p className={styles.scope}>{line.scope}</p>
          </li>
        ))}
      </ul>
      <p className={styles.note}>
        {words.scopeNoteBefore} <code>:*</code> {words.scopeNoteAfter}
      </p>
    </Panel>
  );
}
